import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FitMapLogo } from '@/components/FitMapLogo';
import { NotificationBell } from '@/components/nav/NotificationBell';
import { UserMenu } from '@/components/nav/UserMenu';
import { DashboardIcon, HelpIcon, HistoryIcon, ProfileIcon, StarIcon } from '@/components/nav/navIcons';

const NAV_ITEMS = [
  { to: '/equivalent', label: 'Dashboard', Icon: DashboardIcon },
  { to: '/history', label: 'Fit History', Icon: HistoryIcon },
  { to: '/saved', label: 'Favourites', Icon: StarIcon },
  { to: '/passport', label: 'Profile', Icon: ProfileIcon },
  { to: '/help', label: 'Help', Icon: HelpIcon },
] as const;

const LINK_CLASS = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-1 text-base font-semibold transition-colors ${
    isActive ? 'border-brand text-brand' : 'border-transparent text-muted hover:text-ink'
  }`;

const MOBILE_LINK_CLASS = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-[48px] items-center gap-3 rounded-xl px-3 text-base font-semibold ${
    isActive ? 'bg-brand/10 text-brand' : 'text-ink hover:bg-stone-50'
  }`;

function MenuIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-5 w-5">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
    </svg>
  );
}

export function TopNav() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/equivalent" aria-label="FitMap home" className="shrink-0">
          <FitMapLogo variant="horizontal" />
        </Link>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <UserMenu />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-ink sm:hidden"
          >
            <MenuIcon />
          </button>
        </div>
      </div>

      {/* Desktop/tablet: inline nav row */}
      <nav aria-label="Main" className="mt-5 hidden gap-7 overflow-x-auto border-b border-stone-200 sm:flex">
        {NAV_ITEMS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className={LINK_CLASS}>
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Mobile: collapsible drawer, same destinations */}
      {mobileOpen && (
        <nav id="mobile-nav-menu" aria-label="Main" className="mt-4 space-y-1 rounded-2xl border border-stone-200 bg-white p-2 shadow-card sm:hidden">
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} className={MOBILE_LINK_CLASS} onClick={() => setMobileOpen(false)}>
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
